import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { shortPickerStyles } from "routes/automations/shared/styles";
import {
  IAgent,
  IWithAgentsState,
  selectAgents,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
} from "store/entities/agents";
import {
  IRuleTriggerFollowerAdded,
  IRuleTriggerFollowerID,
  IRuleTriggerFollowerRemoved,
} from "store/entities/rules/interfaces";
import { IWithSessionState } from "store/features/session";

import { changeTrigger, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerFollowerID | IRuleTriggerFollowerAdded | IRuleTriggerFollowerRemoved>;
}

type EqualityType = "is" | "isNot" | "hasBeenAdded" | "hasBeenRemoved";

export const FollowerTrigger: FC<IProps> = ({ input }) => {
  const agentsById = useSelector(selectAgents);
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const agents = useSelector((state: IWithAgentsState & IWithSessionState) =>
    selectAgentsAlphabetically(state, {
      activeOnly: true,
      loggedInAgentOnTop: true,
      excludeViewers: true,
    })
  );

  const equalityOptions = [
    createEqualityOption("is"),
    createEqualityOption("isNot"),
    createEqualityOption("hasBeenAdded"),
    createEqualityOption("hasBeenRemoved"),
  ];

  const selectedEqualityOptions = [createEqualityOption(getEqualityOption(input.value))];

  const agentOptions = agents.map(agent => createAgentOption(agent, loggedInAgent));

  const selectedFollowers: IPickerListItem[] = input.value.in.map(id => {
    const agent = agentsById[id];
    return agent ? createAgentOption(agent, loggedInAgent) : createRemovedAgentOption(id);
  });

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    const selectedFollowerIds = selectedFollowers.map(follower => follower.key);

    switch (selectedOption.key) {
      case "is":
        changeTrigger(input, { type: "followerID", in: selectedFollowerIds, invert: false });
        break;
      case "isNot":
        changeTrigger(input, { type: "followerID", in: selectedFollowerIds, invert: true });
        break;
      case "hasBeenAdded":
        changeTrigger(input, { type: "followerAdded", in: selectedFollowerIds });
        break;
      case "hasBeenRemoved":
        changeTrigger(input, { type: "followerRemoved", in: selectedFollowerIds });
        break;
    }
  };

  const handleSelectFollower = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    changeTriggerValues(input, { in: selectedOptions.map(option => option.key) });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      <Picker
        type="multi"
        options={agentOptions}
        selected={selectedFollowers}
        onSelect={handleSelectFollower}
        placeholder="Select one follower or more…"
      />
    </>
  );
};

const createEqualityOption = (key: EqualityType): IPickerListItem => {
  switch (key) {
    case "is":
      return { key: "is", name: "is" };
    case "isNot":
      return { key: "isNot", name: "is not" };
    case "hasBeenAdded":
      return { key: "hasBeenAdded", name: "has been added" };
    case "hasBeenRemoved":
      return { key: "hasBeenRemoved", name: "has been removed" };
  }
};

const getEqualityOption = (
  value: IRuleTriggerFollowerID | IRuleTriggerFollowerAdded | IRuleTriggerFollowerRemoved
): EqualityType => {
  switch (value.type) {
    case "followerAdded":
      return "hasBeenAdded";
    case "followerRemoved":
      return "hasBeenRemoved";
    case "followerID":
      return value.invert ? "isNot" : "is";
  }
};

const createAgentOption = (agent: IAgent, loggedInAgent: IAgent): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
    listItemBody: (
      <AgentPickerListItem
        agentName={agent.name}
        avatarUrl={agent.avatar}
        isLoggedInAgent={agent.id === loggedInAgent.id}
      />
    ),
  },
});

const createRemovedAgentOption = (agentId: string): IPickerListItem => ({
  key: agentId,
  name: "Follower removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
